import React from "react";
import LoadingBoundary from "@ui/LoadingBoundary";
import SidePanel, { SIDE_PANEL_OPENED_CN } from "@ui/SidePanel";
import SidePanelContent from "@ui/SidePanel/Content";
import classNames from "classnames";
import ProjectComment from "../Comment";
import ErrorBoundary from "../ErrorBoundary";
import ProjectActivity from "./Activity";
import { useSidePanelOpened, useSidePanelTab } from "./useSidePanel";
import type { Project } from "../../api/useProjects";
import { useProjectSource } from "../../api/useSources";
import NavV2 from "@ui/NavV2";
import Tab from "@ui/NavV2/Tab";
import t from "../../i18n";
import { css } from "@linaria/core";
import { colors } from "@ui/Theme";
import { useMatchPattern } from "../../router/helpers/path";

interface Props {
  project: Project;
  hidden?: boolean;
  children: React.ReactNode;
}

export function ProjectGlobalSidePanelLayout({ project, hidden = false, children }: Props) {
  const [selectedTab, setSelectedTab] = useSidePanelTab({
    scope: project.id,
  });
  const [sidePanelOpened] = useSidePanelOpened({
    scope: project.id,
  });
  const dashboardEditionRoute = useMatchPattern(
    "/projects/:projectId/dashboards/:dashboardId/edit",
  );
  const [, setSidePanelOpened] = useSidePanelOpened({
    scope: project.id,
  });
  const source = useProjectSource(project);

  const toggleSidePanel = React.useCallback(() => {
    setSidePanelOpened(!sidePanelOpened);
  }, [sidePanelOpened, setSidePanelOpened]);

  const handleTabClick = React.useCallback<React.MouseEventHandler<HTMLAnchorElement>>(
    e => {
      if (!(e.target instanceof HTMLElement)) throw new Error("Should not happen");
      if (e.target.id !== "comments" && e.target.id !== "activity")
        throw new Error("Should not happen");
      setSelectedTab(e.target.id);
    },
    [setSelectedTab],
  );

  return (
    <>
      {!hidden && (
        <SidePanel
          opened={sidePanelOpened}
          toggle={toggleSidePanel}
          header={
            <NavV2 className={sidePanelNavigation}>
              <Tab
                id="comments"
                onClick={handleTabClick}
                className={classNames({ active: selectedTab === "comments" })}
              >
                {t("sidepanel.tab.comments")}
              </Tab>
              <Tab
                id="activity"
                onClick={handleTabClick}
                className={classNames({ active: selectedTab === "activity" })}
              >
                {t("sidepanel.tab.activity")}
              </Tab>
            </NavV2>
          }
        >
          <ErrorBoundary>
            <LoadingBoundary>
              {selectedTab === "comments" && source ? (
                <ProjectComment project={project} source={source} eventId={null} />
              ) : (
                <div />
              )}
              {selectedTab === "activity" ? (
                <ProjectActivity project={project} eventId={null} />
              ) : (
                <div />
              )}
            </LoadingBoundary>
          </ErrorBoundary>
        </SidePanel>
      )}
      <SidePanelContent
        className={classNames({
          [SIDE_PANEL_OPENED_CN]: sidePanelOpened && !dashboardEditionRoute,
        })}
      >
        {children}
      </SidePanelContent>
    </>
  );
}

const sidePanelNavigation = css`
  border-bottom: 1px solid ${colors.titanium200};
`;
